import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, TableCellRendererParams, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import { Input } from 'src/shared/components/ui/input';
import Loader from 'src/shared/components/loader/Loader';
import StatusFilter from 'src/app/admin/components/status-filter/StatusFilter';
import { ProgressStatus } from 'src/shared/types';
import useCanAdministerOrganisations from 'src/app/admin/hooks/useCanAdministerOrganisations';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { ProjectViews } from 'src/routes/routesConfig';
import { useCustomerProjectsInBCQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import { buildGraphProjectsInput, GraphProject } from 'src/app/admin/state/api/queries/projectAdminQuery';
import { useCustomerProjectDefinitionsQuery } from 'src/app/admin/state/api/adminGraphSlice';
import { hasHabitatInsightsFeature } from 'src/shared/utils/featureHelpers';
import { FeatureType } from 'src/shared/types';
import CountryCell from './components/country-cell/CountryCell';
import FeatureChips, { featuresSummary } from './components/feature-chips/FeatureChips';
import ProjectRowActions from './components/project-row-actions/ProjectRowActions';
import PortfolioToggleCell from './components/portfolio-toggle-cell/PortfolioToggleCell';
import DeleteDefinitionDialog from '../../../components/delete-definition-dialog/DeleteDefinitionDialog';
import DeleteProjectDialog from '../../../components/delete-project-dialog/DeleteProjectDialog';
import ProjectStatsSheet from '../../../components/project-stats-sheet/ProjectStatsSheet';
import ManageProjectSheet from '../../../components/manage-project-sheet/ManageProjectSheet';
import { mergeProjectsWithDefinitions, MergedProjectRow } from '../../mergeProjectsWithDefinitions';

type OrgProjectsTableProps = {
    customerId: string;
    /** Page-level actions, laid out to the right of the views and search as the portfolio does. */
    actions?: React.ReactNode;
};

const NOT_SET = '—';

type ProjectView = 'active' | 'draft';

/**
 * A project is Active once its definition is complete. Until then it is a Draft — whether
 * it has no definition at all, or one that was begun and left unfinished.
 */
const isActive = (row: MergedProjectRow) => row.hasDefinition && row.definition?.status !== ProgressStatus.PENDING;

const buildColumns = (): DataTableColumns => [
    { columnId: 'projectName', title: 'Project Name', isSortable: true, isSearchable: true },
    // The project cards identify a project by its code, so this table does too rather
    // than exposing the internal projectId alongside it.
    { columnId: 'projectCode', title: 'Project code', isSortable: true, isSearchable: true },
    {
        columnId: 'country',
        title: 'Sampling country',
        renderer: ({ cellData }: TableCellRendererParams) => <CountryCell country={String(cellData ?? '')} />,
    },
    // The row holds the same text the cell shows, so sorting and searching agree with the eye.
    {
        columnId: 'features',
        title: 'Features',
        isSortable: true,
        isSearchable: true,
        renderer: ({ cellData }: TableCellRendererParams) => <FeatureChips summary={String(cellData ?? '')} />,
    },
    {
        columnId: 'portfolio',
        // Only projects with Habitat Insights ever get a live toggle here (see PortfolioToggleCell) —
        // without it a toggled-on project could sit unresolved forever, since nothing would ever
        // produce the data the portfolio aggregate step is waiting for.
        title: 'Portfolio',
    },
    { columnId: 'actions', title: 'Actions' },
    // Carried so a row click knows which project it is, without showing the internal id.
    { columnId: 'projectId', title: 'Project ID', isUnique: true },
];

const columns = buildColumns();

const HIDDEN_COLUMNS = new Set(['projectId']);

const GNPProjectsTable = ({ customerId, actions }: OrgProjectsTableProps) => {
    const [searchText, setSearchText] = useState('');
    const [view, setView] = useState<ProjectView>('active');
    const [renamingProject, setRenamingProject] = useState<MergedProjectRow | null>(null);
    const [statsProject, setStatsProject] = useState<MergedProjectRow | null>(null);
    const [deletingDefinitionFor, setDeletingDefinitionFor] = useState<MergedProjectRow | null>(null);
    const [deletingProject, setDeletingProject] = useState<MergedProjectRow | null>(null);
    const navigate = useNavigate();
    const { canManage, canDelete } = useCanAdministerOrganisations();

    /**
     * Identifies the project through the URL, which `useProjectDefinition` reads on mount.
     * Seeding the wizard's redux form state beforehand does not survive: StrictMode's
     * remount fires the wizard's unmount cleanup, which resets it.
     */
    const startDefinition = (row: MergedProjectRow) =>
        navigate(navRoutesConfig[ProjectViews.NEW_PROJECT_DEFINITION]({ customerId, projectId: row.projectId }));

    /** Only projects that have a definition have somewhere for a row click to go. */
    const openDefinition = (rowData: TableRowData) => {
        const projectId = rowData[rowData.length - 1] as string;
        const row = rows.find(candidate => candidate.projectId === projectId);
        if (!row?.hasDefinition) {
            return;
        }
        navigate(navRoutesConfig[ProjectViews.PROJECT_DEFINITION]({ customerId, projectId }));
    };

    const { data: graphProjectsData, isLoading: isLoadingProjects } = useCustomerProjectsInBCQuery(buildGraphProjectsInput(customerId));
    // Carries includeInPortfolio (DD-6079) alongside every other definition field - re-read on
    // every load rather than trusted from an optimistic local flip, so whoever opens this page
    // next sees the real state, not just whatever the last person to click a toggle left behind.
    const { data: definitions, isLoading: isLoadingDefinitions } = useCustomerProjectDefinitionsQuery({ customerId });

    // Both stores must have answered: a project looks definition-less while the
    // definition list is still in flight.
    const isLoading = isLoadingProjects || isLoadingDefinitions;

    const graphProjects = useMemo(
        () => ((graphProjectsData?.customerProjectsInBC?.projects ?? []) as (GraphProject | null)[]).filter(Boolean) as GraphProject[],
        [graphProjectsData]
    );

    const allRows = useMemo(() => mergeProjectsWithDefinitions(graphProjects, definitions ?? []), [graphProjects, definitions]);

    const activeRows = useMemo(() => allRows.filter(isActive), [allRows]);
    const draftRows = useMemo(() => allRows.filter(row => !isActive(row)), [allRows]);
    const rows = view === 'active' ? activeRows : draftRows;

    const viewOptions = useMemo(
        () => [
            { value: 'active' as const, label: `Active (${activeRows.length})` },
            { value: 'draft' as const, label: `Draft (${draftRows.length})` },
        ],
        [activeRows.length, draftRows.length]
    );

    const data = useMemo(
        () =>
            rows.map(row => [
                row.projectName ?? NOT_SET,
                row.projectCode ?? NOT_SET,
                row.definition?.country ?? '',
                featuresSummary(row),
                <PortfolioToggleCell
                    key={`portfolio-${row.projectId}`}
                    projectId={row.projectId}
                    projectName={row.projectName ?? row.projectId}
                    hasHabitatInsights={hasHabitatInsightsFeature((row.definition?.features ?? []) as FeatureType[])}
                    inPortfolio={row.definition?.includeInPortfolio ?? false}
                />,
                <ProjectRowActions
                    key={`actions-${row.projectId}`}
                    row={row}
                    customerId={customerId}
                    canManage={canManage}
                    canDelete={canDelete}
                    onRename={setRenamingProject}
                    onShowStats={setStatsProject}
                    onStartDefinition={startDefinition}
                    onDeleteDefinition={setDeletingDefinitionFor}
                    onDeleteProject={setDeletingProject}
                />,
                row.projectId,
            ]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, customerId, canManage, canDelete]
    );

    if (isLoading) {
        return (
            <div className='flex justify-center py-16'>
                <Loader />
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex items-center justify-between gap-4 flex-wrap' data-testid='org-projects-toolbar'>
                <div className='flex items-center gap-3'>
                    <StatusFilter options={viewOptions} value={view} onChange={setView} ariaLabel='Filter projects by status' />
                    <div className='relative w-72'>
                        <SearchIcon className='w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none' />
                        <Input
                            roundness='Round'
                            type='search'
                            value={searchText}
                            onChange={event => setSearchText(event.target.value)}
                            placeholder='Search projects ...'
                            aria-label='Search projects'
                            data-testid='org-projects-search-input'
                            className='pl-9'
                        />
                    </div>
                </div>
                {actions && <div className='flex items-center gap-2'>{actions}</div>}
            </div>
            <div className='[&_tbody_tr]:cursor-pointer'>
                <DataTable
                    columns={columns}
                    state={{ data, searchText, hiddenColumnIds: HIDDEN_COLUMNS }}
                    onRowSelect={openDefinition}
                    emptyStateMessage={
                        view === 'active' ? 'No projects with a completed definition yet.' : 'No projects awaiting a definition.'
                    }
                />
            </div>
            {deletingDefinitionFor && (
                <DeleteDefinitionDialog
                    open
                    onOpenChange={isOpen => !isOpen && setDeletingDefinitionFor(null)}
                    projectId={deletingDefinitionFor.projectId}
                    projectName={deletingDefinitionFor.projectName ?? deletingDefinitionFor.projectId}
                />
            )}
            {deletingProject && (
                <DeleteProjectDialog
                    open
                    onOpenChange={isOpen => !isOpen && setDeletingProject(null)}
                    projectId={deletingProject.projectId}
                    projectName={deletingProject.projectName ?? deletingProject.projectId}
                />
            )}
            {statsProject && (
                <ProjectStatsSheet
                    open
                    onOpenChange={isOpen => !isOpen && setStatsProject(null)}
                    projectId={statsProject.projectId}
                    projectName={statsProject.projectName ?? statsProject.projectId}
                />
            )}
            {renamingProject && (
                <ManageProjectSheet
                    open
                    onOpenChange={isOpen => !isOpen && setRenamingProject(null)}
                    projectId={renamingProject.projectId}
                    projectName={renamingProject.projectName ?? ''}
                />
            )}
        </div>
    );
};

export default GNPProjectsTable;
